/*
 * @Description: file content
 * @Author: ljrong
 * @Date: 2024-05-08 14:35:28
 * @LastEditors: Lxc 1533761120@qq.com
 * @LastEditTime: 2025-06-17 11:51:35
 */
import { defineStore } from 'pinia'
import type { ScrollbarInst } from 'naive-ui'
import dayjs from 'dayjs'
import { EIntelligentType, ELanguage, EMessageType } from '../constant'
import type { IConfig } from '../models/message'
import { Message } from '../models/message'
import type { IGetChatId, IRangeParams, ISearchParams } from '../api'
import { ERangeType, getChatId, getFolderDesign, getMaterialSuggestion, getRange, searchDocData } from '../api'
import { getNowDate } from '../ultils'
import type { PreQuestionConfig } from './asst-store'
import { asstStore } from './asst-store'
import { drawAsstV2Store, useAssistantLimitStore } from './index'
import { useUserStore } from '@/store'
import emitter, { events } from '@/event-bus'

export const assistantListStore = defineStore('assistantListStore', () => {
  const userStore = useUserStore()
  const useAssistantLimit = useAssistantLimitStore()
  const drawStore = drawAsstV2Store()

  const asstMessageList = ref<Message[]>([])

  const count = ref(-1)
  function increment() {
    count.value--
  }

  const preQuestionData = ref({
    docList: [],
    docReplyType: 2,
  })

  const messageScrollRef = ref<ScrollbarInst>()
  const listDom = ref<HTMLDivElement>()

  const addMessage = (type: EMessageType, config?: IConfig) => {
    const message = new Message(type, config)

    asstMessageList.value.push(message)
    toBottom()

    return message
  }

  function toBottom() {
    if (messageScrollRef.value) {
      nextTick(() => {
        messageScrollRef.value?.scrollTo({
          top: 300000,
        })
      })
    }
  }
  const assist = asstStore()

  const DIGEST_F = 'YYYY/MM/DD HH:mm:ss dddd'

  function _needLangue(s: string) {
    return `${s}${assist.replyLanguage === ELanguage.ZH ? '\nPlease reason and answer in Chinese' : '\nPlease reason and answer in English'}`
  }

  async function _getRangeParams(p: IRangeParams, replyMessage?: Message, digest?: string) {
    const rangeRes = await getRange(p)
    preQuestionData.value = rangeRes
    const realContent = _needLangue(rangeRes.searchResult)

    if (replyMessage) {
      replyMessage.updateKnowledgeFlag(rangeRes.knowledgeFlag)
      if (!assist.chatConfig.currentPreQuestion) {
        replyMessage.updateDocList(rangeRes.docList)
        replyMessage.setCocReplyType(rangeRes.docReplyType)
      }
    }

    const tureDigest = (digest && digest.indexOf('null') > 0 ? '' : digest)
    return {
      knowledgeFlag: rangeRes.knowledgeFlag,
      content: p.question,
      contentGpt: realContent,
      rangeId: rangeRes.rangeId,
      placeholders: {
        '{digest}': `\n${tureDigest?.replace(/<span class="quote" data-pages="(\d?.+)">\[\d+\]<\/span>/g, '')?.replace(/\d+\./g, '')}` || '',
        '{date}': `\n当前日期:${dayjs().format(DIGEST_F)}`,
      },
    }
  }

  async function _getFolderSummary(type: 'file' | 'knowledge') {
    const isDigest = type === 'file' ? assist.chatConfig.currentFindFile?.isDigest : assist.chatConfig.currentPreQuestion?.isDigest
    let digest = ''
    if (isDigest) {
      digest = (type === 'file' ? assist.chatConfig.currentFindFile?.digest : assist.chatConfig.currentPreQuestion?.digest) || ''
    } else {
      const dirId = type === 'file' ? assist.chatConfig.currentFindFile?.ranges[0].businessId : assist.chatConfig.currentPreQuestion?.businessId

      if (dirId) {
        const res = await getFolderDesign({ dirId })
        digest = res.digest || ''
        if (type === 'knowledge' && assist.chatConfig.currentPreQuestion) {
          assist.chatConfig.currentPreQuestion.digest = digest
          assist.chatConfig.currentPreQuestion.isDigest = true
        } else if (assist.chatConfig.currentFindFile) {
          assist.chatConfig.currentFindFile.digest = digest
          assist.chatConfig.currentFindFile.isDigest = true
        }
      }
    }
    return digest ? `${type === 'file' ? '\n 本文主要内容如下：' : '\n本文件夹主要内容如下：'}${digest}` : ''
  }

  async function _GetChantIDParams(content: string, message: Message, replyMessage: Message, realQuestion?: string) {
    let param: Partial<IGetChatId> = {
      sessionId: assist.chatConfig.sessionID,
      historyId: assist.chatConfig.historyID,
      businessType: assist.chatConfig.businessType || assist.chatConfig.currentType,
      enterpriseId: assist.currentWorkspace.workspaceId,
      createTime: message?.createTime ? message.createTime : getNowDate(),
      model: assist.chatConfig.model || null, // 模型 null: beautyGPT; 8: R1
    }
    if (assist.chatConfig.knowledgeFlag !== null) {
      param.knowledgeFlag = assist.chatConfig.knowledgeFlag
      replyMessage?.updateKnowledgeFlag(assist.chatConfig.knowledgeFlag)
    }
    if (assist.chatConfig.currentFindFile) {
      const range = await _getRangeParams({
        question: realQuestion ?? content,
        ranges: [
          {
            allSelect: true,
            businessId: assist.chatConfig.currentFindFile.ranges[0].businessId,
            businessName: assist.chatConfig.currentFindFile.ranges[0].businessName,
            docs: [],
            type: assist.chatConfig.currentFindFile.ranges[0].type || ERangeType.FOLDER,
          },
        ],
        businessType: 2,
      }, replyMessage, await _getFolderSummary('file'))

      param = Object.assign({}, param, range)
    } else if (assist.chatConfig.currentPreQuestion) {
      const range = await _getRangeParams({
        question: realQuestion ?? content,
        ranges: [
          {
            allSelect: true,
            businessId: assist.chatConfig.currentPreQuestion.businessId,
            businessName: assist.chatConfig.currentPreQuestion.businessName,
            docs: [],
            type: assist.chatConfig.currentPreQuestion.type || ERangeType.FOLDER,
          },
        ],
        businessType: 2,
      }, replyMessage, await _getFolderSummary('knowledge'))

      param = Object.assign({}, param, range, { content })
    } else if (assist.chatConfig.currentType === EIntelligentType.GLOBAL) {
      const realContent = _needLangue(content)

      param = Object.assign({}, param, {
        content: content ?? realQuestion,
        contentGpt: realContent,
        placeholders: {
          '{date}': `\n当前日期:${dayjs().format(DIGEST_F)}`,
        },
      })
    } else if (assist.chatConfig.currentType === EIntelligentType.FILE) {
      const range = await _getRangeParams({
        question: realQuestion ?? content,
        ranges: [{
          businessId: assist.chatConfig.fileID!,
          businessName: assist.chatConfig.fileName!,
          docs: [
            {
              fileId: assist.chatConfig.fileID!,
              fileName: assist.chatConfig.fileName!,
            },
          ],
          type: ERangeType.FILE,
        }],
        businessType: 1,
      }, replyMessage, `\n本文主要内容如下：${assist.chatConfig.fileInfo?.fullDigest}`)
      param = Object.assign({}, param, range)
    }

    return param
  }

  function handleRequestFail(e: { code: number; resultMsg: string }, messageObj: {
    message?: Message
    useMessage?: Message
  }) {
    useAssistantLimit.getAssistantLimit()
    if ([10008, 60004, 60008].includes(e.code)) {
      try {
        const { message, useMessage } = messageObj
        const eMessage = JSON.parse(e.resultMsg)

        const replyMessage = message || addMessage(EMessageType.REPLY, {
          createTime: useMessage?.createTime,
          isLoading: true,
        })

        replyMessage.updateContent(eMessage.text)
        replyMessage.updateRequestId(eMessage.requestId)
        eMessage.staffImg && replyMessage.updateImg([eMessage.staffImg])

        if (useMessage) {
          useMessage.updateRequestId(eMessage.requestId)
        }

        increment()
        closeEventSource()
      } catch (error) {
        closeEventSource()
        console.log('🚀 ~ _handleRequestFail ~ error:', error)
      }
    } else if ([90004, 90005, 90006].includes(e.code)) {
      useAssistantLimit.updateInsufficientEnergyModel(true)
      closeEventSource()
      console.log('🚀 ~ _handleRequestFail ~ err:', e)
    } else {
      closeEventSource()
      console.log('🚀 ~ _handleRequestFail ~ e:', e)
    }
  }

  async function fetchReply(content: string, realQuestion?: string) {
    assist.loadingMessage = true

    const message = addMessage(EMessageType.QUESTION, {
      content,
    })

    const replyMessage = addMessage(EMessageType.REPLY, {
      model: assist.chatConfig.model,
      createTime: message.createTime,
      isLoading: true,
      question: content,
    })

    // console.log('assist.chatConfig :', assist.chatConfig)
    const param = await _GetChantIDParams(content, message, replyMessage, realQuestion)
    // 如果是停止状态，则不进行流式处理
    if (!assist.loadingMessage) {
      return
    }

    // console.log('🚀 ~ fetchReply ~ param:', param)
    if (param) {
      await getChatId(param as IGetChatId)?.then((res) => {
        // console.log('🚀 ~ getChatId ~ res:', res)

        _handleStream(res.streamId, { message, replyMessage })

        if (assist.chatConfig.currentPreQuestion) {
          replyMessage.updateDocList(preQuestionData.value.docList)

          replyMessage.setCocReplyType(preQuestionData.value.docReplyType)
        }

        if (replyMessage.docList.length > 0) {
          emitter.emit(events.showAppAiQuoteDialog, {
            index: 1,
            quoteList: preQuestionData.value.docList,
          })
        } else {
          emitter.emit(events.hideAppAiQuoteDialog)
        }
      }).catch((e) => {
        if ([90004, 90005, 90006].includes(e.code)) {
          useAssistantLimit.updateInsufficientEnergyModel(true)
          closeEventSource()
          console.log('🚀 ~ fetchReply ~ err:', e)
        } else {
          // const message = addMessage(EMessageType.QUESTION, {
          //   content,
          // })

          // const replyMessage = addMessage(EMessageType.REPLY, {
          //   createTime: message.createTime,
          //   isLoading: true,
          // })
          handleRequestFail(e, {
            message: replyMessage,
          })
        }
      }).finally(() => {
        userStore.getBalance()
        useAssistantLimit.getAssistantLimit()
      })
    }
  }

  const eventSource = ref<any>()
  function _handleStream(id: string, msgObj: {
    message: Message
    replyMessage: Message
  }) {
    eventSource.value = new EventSource(
      `/assistant/intelligentAssistant/chat/conversation/stream?streamId=${id}`,
    )

    const init = true
    let reasoningDuration = 0
    eventSource.value.onmessage = function (event: any) {
      const data = event.data
      try {
        const resp = JSON.parse(data)
        if (init) {
          msgObj.message.updateRequestId(resp.requestId)
          msgObj.replyMessage.updateRequestId(resp.requestId)
          msgObj.replyMessage.updateFinish(resp.delta?.finish)
        }

        if (resp.delta.reasoning === 1) {
          msgObj.replyMessage.joinReasoningContent(resp.delta.reasoningContent)
          reasoningDuration = resp.delta.reasoningDuration
        }
        if (resp.delta.reasoning === 0) {
          if (msgObj.replyMessage.reasoningContent) {
            // 深度思考结束, 计算思考时长
            msgObj.replyMessage.updateReasoningTime(reasoningDuration)
          }
          msgObj.replyMessage.joinContent(resp.delta.content)
        }
        increment()

        if ((resp.delta?.finish !== null && resp.delta?.finish !== '') || resp.errCode !== 0) {
          userStore.getBalance()
          useAssistantLimit.getAssistantLimit()
          closeEventSource()
        }

        toBottom()
      } catch (e) {
        closeEventSource()
        console.log('🚀 ~ _handleStream ~ e:', e)
      }
    }

    eventSource.value.onerror = function (event: any) {
      console.log('🚀 ~ _handleStream ~ event:', event)
      closeEventSource()
    }
  }

  async function _isChangeSession() {
    if (assist.chatConfig.currentSession === 215) {
      await assist.newSession(216)
      assist.chatConfig.currentSession = 216
    }
  }

  async function handleQuestion(params: PreQuestionConfig, realQuestion?: string) {
    await _isChangeSession()

    assist.chatConfig.currentPreQuestion = params
    assist.chatConfig.currentFindFile = null

    await fetchReply(params.question, realQuestion)
  }

  async function handleFindFile(params: Omit<ISearchParams, 'createTime' | 'enterpriseId' | 'historyId' | 'businessType'>) {
    await _isChangeSession()
    assist.chatConfig.currentFindFile = params
    assist.chatConfig.currentPreQuestion = null

    const nowDate = getNowDate()

    const message = addMessage(EMessageType.QUESTION, {
      createTime: nowDate,
      content: params.question,
    })
    const reply = addMessage(EMessageType.REPLY, {
      createTime: nowDate,
      isLoading: true,
    })

    assist.loadingMessage = true

    try {
      const res = await searchDocData({
        ...params,
        createTime: nowDate,
        historyId: assist.chatConfig.historyID,
        enterpriseId: assist.currentWorkspace.workspaceId,
      })

      useAssistantLimit.getAssistantLimit()

      // const message = addMessage(EMessageType.QUESTION, {
      //   createTime: nowDate,
      //   content: params.question,
      // })

      // const reply = addMessage(EMessageType.REPLY, {
      //   createTime: nowDate,
      //   isLoading: true,
      // })

      message.updateRequestId(res.requestId)

      if (res.noResultReply) {
        reply.updateContent(res.noResultReply)
        increment()
      } else {
        reply.updateContent('已为你找到如下文件/资料：')
        reply.updateFileList(res.simpleFileVos)
        increment()
      }
      reply.updateRequestId(res.requestId)
    } catch (e) {
      if ([90004, 90005, 90006].includes(e.code)) {
        useAssistantLimit.updateInsufficientEnergyModel(true)
        closeEventSource()
        console.log('🚀 ~ handleFindFile ~ err:', e)
      } else {
        // const message = addMessage(EMessageType.QUESTION, {
        //   createTime: nowDate,
        //   content: params.question,
        // })

        // const reply = addMessage(EMessageType.REPLY, {
        //   createTime: nowDate,
        //   isLoading: true,
        // })
        handleRequestFail(e, {
          useMessage: message,
          message: reply,
        })
      }
    }

    assist.loadingMessage = false
    toBottom()
  }

  async function handleMaterial(params: any) {
    const nowDate = getNowDate()

    const message = addMessage(EMessageType.QUESTION, { createTime: nowDate, content: params.question })
    try {
      const res = await getMaterialSuggestion({
        ...params,
        createTime: nowDate,
        historyId: assist.chatConfig.historyID,
      })

      await useAssistantLimit.getAssistantLimit()

      message.updateRequestId(res.requestId)

      addMessage(EMessageType.MATERIAL, {
        createTime: nowDate,
        content: `通过“${params.mechanism}”达到${params.effect}功效的原料主要有：`,
        materials: res.materialName,
        requestId: res.requestId,
      })
    } catch (e) {
      if ([90004, 90005, 90006].includes(e.code)) {
        useAssistantLimit.updateInsufficientEnergyModel(true)
        closeEventSource()
        console.log('🚀 ~ fetchReply ~ err:', e)
      } else {
        handleRequestFail(e, {
          useMessage: message,
        })
      }
    }
  }

  function closeEventSource() {
    eventSource.value?.close()

    const assist = asstStore()
    assist.loadingMessage = false

    _lastLegal()
  }

  /**
   *  判断最后一条信息是否合法
   */
  function _lastLegal() {
    const lastMessage = asstMessageList.value[asstMessageList.value.length - 1]

    if (lastMessage && lastMessage.content === '' && lastMessage.reasoningContent === '' && lastMessage.isLoading) {
      asstMessageList.value.pop()
    } else {
      lastMessage?.stopThinking()
    }
  }

  const reset = () => {
    // if (assist.loadingMessage) {
    closeEventSource()
    // }
    console.log('reset')
    asstMessageList.value = []
  }

  return {
    addMessage,
    reset,
    fetchReply,
    _needLangue,
    handleFindFile,
    handleQuestion,
    closeEventSource,
    handleRequestFail,
    count,
    asstMessageList,
    messageScrollRef,
    listDom,
    handleMaterial,
  }
})
